/**
 * Portal paths for a notification target.
 *
 * Lives in `shared` because three different senders build these URLs: the backend
 * (which stamps a ready-made `path` into the FCM payload), the portals' service
 * workers, and the extension. Copies of the portals' route structure would drift
 * the moment either portal reorganises its routes, and nothing type-checks a
 * hand-built string against `App.tsx`. Keep this the only place that knows the
 * shapes.
 */

import {
  findAppByPortalSlug,
  isPortalApp,
  type PortalApp,
  type PushDevicePlatform,
  type RegisterablePushPlatform,
} from './app-registry';

export type { PortalApp, PushDevicePlatform, RegisterablePushPlatform };
export { isPortalApp };

export type NotificationTargetRef = {
  targetType?: string | null;
  targetId?: string | number | null;
  orgId?: string | number | null;
  workspaceId?: string | number | null;
};

function positiveInt(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Workspace-scoped prefix every in-app portal route hangs off. Returns null when
 * the caller cannot name both the org and the workspace, which is the one case a
 * URL genuinely cannot be built for. Identical across both portals.
 */
export function portalWorkspaceBase(target: NotificationTargetRef): string | null {
  const orgId = positiveInt(target.orgId);
  const workspaceId = positiveInt(target.workspaceId);
  if (!orgId || !workspaceId) return null;
  return `/org/${orgId}/workspace/${workspaceId}`;
}

/**
 * Deep link for a notification, or the workspace dashboard when the target has no
 * view of its own in that app. `thread` is deliberately in that second group:
 * comment threads are rendered inside a task or contact, and the notification does
 * not carry the parent, so the dashboard is the honest landing spot.
 */
export function portalNotificationPath(
  app: PortalApp,
  target: NotificationTargetRef
): string | null {
  const base = portalWorkspaceBase(target);
  if (!base) return null;

  const targetId = positiveInt(target.targetId);
  if (targetId && target.targetType) {
    const entry = findAppByPortalSlug(app);
    const route = entry?.routes[target.targetType];
    if (route) return `${base}${route(targetId)}`;
  }
  return `${base}/dashboard`;
}

/** The full notification inbox. Same route in both portals. */
export function portalNotificationsInboxPath(target: NotificationTargetRef): string | null {
  const base = portalWorkspaceBase(target);
  return base && `${base}/notifications`;
}

/** Joins an origin to a portal path without doubling or dropping the slash. */
export function portalUrl(origin: string, path: string) {
  return `${origin.replace(/\/+$/, '')}${path}`;
}
